// Общие для модулей campaign-setup/ типы, иконки и мелкие
// презентационные компоненты. Вынесено из CampaignSetupPage.tsx без
// изменения поведения (Фаза 1, разбор монолита).

import { useState, useEffect, useMemo, type ReactNode } from "react";
import { api, type SetupMember } from "../api";
import { splitNarrativeParagraphs } from "../text";

type ChronicleSection =
  "chapters" | "threads" | "threats" | "council" | "prologue";
type WorldSection =
  | "overview"
  | "infrastructure"
  | "threats"
  | "continuity"
  | "resources"
  | "indicators"
  | "institutions"
  | "factions";
type CouncilSection = "members" | "relationships";
type DossierSection = "intro" | "goal" | "briefing";
type PageAction = (
  label: string,
  run: () => Promise<unknown>,
) => Promise<boolean>;
type IconName =
  | "cycle"
  | "book"
  | "world"
  | "users"
  | "lock"
  | "sparkles"
  | "image"
  | "resources"
  | "activity"
  | "institution"
  | "faction"
  | "vote"
  | "check"
  | "avatar"
  | "target"
  | "motive"
  | "briefing"
  | "power"
  | "role"
  | "settings";
const GAME_UI_ASSET_BASE = "/assets/game-ui";
function MemberAvatar({
  campaignId,
  member,
  large = false,
  xlarge = false,
}: {
  campaignId: string;
  member: SetupMember;
  large?: boolean;
  xlarge?: boolean;
}) {
  const [customUrl, setCustomUrl] = useState<string | null>(null);
  const [imageFailed, setImageFailed] = useState(false);
  useEffect(() => {
    setImageFailed(false);
    if (member.avatar.kind !== "CUSTOM") {
      setCustomUrl(null);
      return;
    }
    let cancelled = false;
    let objectUrl: string | null = null;
    void api
      .campaignAvatarContent(campaignId, member.campaignMemberId)
      .then((blob) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setCustomUrl(objectUrl);
      })
      .catch(() => {
        if (!cancelled) setCustomUrl(null);
      });
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [
    campaignId,
    member.campaignMemberId,
    member.avatar.kind,
    member.avatar.updatedAt,
    member.avatar.telegramUrl,
    member.avatar.styleStatus,
  ]);

  const src = imageFailed
    ? null
    : member.avatar.kind === "TELEGRAM"
      ? member.avatar.telegramUrl
      : customUrl;
  const initials =
    member.displayName
      .split(/\s+/)
      .slice(0, 2)
      .map((part) => part[0]?.toUpperCase())
      .join("") || "И";
  const className = `cc-avatar${large ? " large" : ""}${xlarge ? " xlarge" : ""}`;
  return (
    <span className={className}>
      {src ? (
        <img
          src={src}
          alt=""
          referrerPolicy="no-referrer"
          onError={() => setImageFailed(true)}
        />
      ) : (
        <b>{initials}</b>
      )}
    </span>
  );
}
function Spinner({ small = false }: { small?: boolean }) {
  return (
    <span className={`cc-spinner${small ? " small" : ""}`} aria-hidden="true" />
  );
}
function ProgressBar({
  value,
  indeterminate = false,
}: {
  value?: number;
  indeterminate?: boolean;
}) {
  return (
    <div className={`cc-progress${indeterminate ? " indeterminate" : ""}`}>
      <i
        style={
          indeterminate
            ? undefined
            : { width: `${Math.max(0, Math.min(100, value ?? 0))}%` }
        }
      />
    </div>
  );
}
function BlockHeader({
  eyebrow,
  title,
  icon,
}: {
  eyebrow: string;
  title: string;
  icon?: IconName;
}) {
  return (
    <div className="cc-block-header">
      {icon && <IconBadge icon={icon} />}
      <div>
        <div className="eyebrow">{eyebrow}</div>
        <h2>{title}</h2>
      </div>
    </div>
  );
}
function IconBadge({ icon }: { icon: IconName }) {
  return (
    <span className="cc-icon-badge" aria-hidden="true">
      <Icon name={icon} />
    </span>
  );
}
function Icon({ name }: { name: IconName }) {
  const paths: Record<IconName, ReactNode> = {
    cycle: (
      <>
        <circle cx="12" cy="12" r="9" />
        <path d="M12 7v5l3 2M7 3.8 4.8 2M17 3.8 19.2 2" />
      </>
    ),
    book: (
      <>
        <path d="M4 5.5A3.5 3.5 0 0 1 7.5 2H12v17H7.5A3.5 3.5 0 0 0 4 22V5.5Z" />
        <path d="M20 5.5A3.5 3.5 0 0 0 16.5 2H12v17h4.5A3.5 3.5 0 0 1 20 22V5.5Z" />
      </>
    ),
    world: (
      <>
        <circle cx="12" cy="12" r="9" />
        <path d="M3 12h18M12 3a14 14 0 0 1 0 18M12 3a14 14 0 0 0 0 18" />
      </>
    ),
    users: (
      <>
        <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
        <circle cx="9" cy="7" r="4" />
        <path d="M22 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
      </>
    ),
    lock: (
      <>
        <rect x="4" y="10" width="16" height="11" rx="2" />
        <path d="M8 10V7a4 4 0 0 1 8 0v3M12 14v3" />
      </>
    ),
    sparkles: (
      <>
        <path d="m12 3 1.2 3.2L16 7.5l-2.8 1.3L12 12l-1.2-3.2L8 7.5l2.8-1.3L12 3ZM5 13l.8 2.2L8 16l-2.2.8L5 19l-.8-2.2L2 16l2.2-.8L5 13ZM19 12l.8 2.2L22 15l-2.2.8L19 18l-.8-2.2L16 15l2.2-.8L19 12Z" />
      </>
    ),
    image: (
      <>
        <rect x="3" y="4" width="18" height="16" rx="2" />
        <circle cx="8.5" cy="9" r="1.5" />
        <path d="m21 15-5-5L5 20" />
      </>
    ),
    resources: (
      <>
        <path d="M4 7h16v13H4zM2 4h20v3H2zM9 11h6" />
      </>
    ),
    activity: <path d="M3 12h4l2-7 4 14 2-7h6" />,
    institution: (
      <>
        <path d="m3 9 9-6 9 6M5 10v9M9 10v9M15 10v9M19 10v9M3 21h18" />
      </>
    ),
    faction: (
      <>
        <path d="M5 21V4M5 5h12l-2 4 2 4H5" />
      </>
    ),
    vote: (
      <>
        <path d="M4 4h16v5H4zM3 9h18v11H3zM8 14h8" />
      </>
    ),
    check: (
      <>
        <circle cx="12" cy="12" r="9" />
        <path d="m8 12 2.5 2.5L16 9" />
      </>
    ),
    avatar: (
      <>
        <circle cx="12" cy="8" r="4" />
        <path d="M4 21a8 8 0 0 1 16 0" />
      </>
    ),
    target: (
      <>
        <circle cx="12" cy="12" r="9" />
        <circle cx="12" cy="12" r="5" />
        <circle cx="12" cy="12" r="1" />
      </>
    ),
    motive: (
      <>
        <path d="M4 5h16v11H4zM8 16v3M16 16v3M7 10h.01M17 10h.01M9 13c1.5 1 4.5 1 6 0" />
      </>
    ),
    briefing: (
      <>
        <path d="M6 3h12v18H6zM9 7h6M9 11h6M9 15h4" />
      </>
    ),
    power: <path d="m13 2-8 12h7l-1 8 8-12h-7l1-8Z" />,
    role: (
      <>
        <path d="M12 3 4 7v5c0 5 3.4 8 8 9 4.6-1 8-4 8-9V7l-8-4Z" />
        <path d="M9 12h6M12 9v6" />
      </>
    ),
    settings: (
      <>
        <circle cx="12" cy="12" r="3" />
        <path d="M19.4 15a1.7 1.7 0 0 0 .34 1.88l.06.06-2.83 2.83-.06-.06A1.7 1.7 0 0 0 15 19.4a1.7 1.7 0 0 0-1 .6 1.7 1.7 0 0 0-.4 1.1V21h-4v-.1A1.7 1.7 0 0 0 8.6 19.4a1.7 1.7 0 0 0-1.88.34l-.06.06-2.83-2.83.06-.06A1.7 1.7 0 0 0 4.6 15a1.7 1.7 0 0 0-.6-1 1.7 1.7 0 0 0-1.1-.4H3v-4h.1A1.7 1.7 0 0 0 4.6 8.6a1.7 1.7 0 0 0-.34-1.88l-.06-.06 2.83-2.83.06.06A1.7 1.7 0 0 0 9 4.6a1.7 1.7 0 0 0 1-.6 1.7 1.7 0 0 0 .4-1.1V3h4v.1A1.7 1.7 0 0 0 15.4 4.6a1.7 1.7 0 0 0 1.88-.34l.06-.06 2.83 2.83-.06.06A1.7 1.7 0 0 0 19.4 9a1.7 1.7 0 0 0 .6 1 1.7 1.7 0 0 0 1.1.4h.1v4h-.1A1.7 1.7 0 0 0 19.4 15Z" />
      </>
    ),
  };
  return (
    <svg
      className="cc-icon"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {paths[name]}
    </svg>
  );
}
function ParagraphText({
  text,
  className = "",
}: {
  text?: string;
  className?: string;
}) {
  const paragraphs = useMemo(() => splitIntoParagraphs(text ?? ""), [text]);
  if (paragraphs.length === 0) return null;
  return (
    <div className={`paragraph-text ${className}`.trim()}>
      {paragraphs.map((paragraph, index) => (
        <p key={`${index}-${paragraph.slice(0, 20)}`}>{paragraph}</p>
      ))}
    </div>
  );
}
function splitIntoParagraphs(text: string): string[] {
  return splitNarrativeParagraphs(text);
}

export {
  Icon,
  IconBadge,
  BlockHeader,
  ParagraphText,
  splitIntoParagraphs,
  ProgressBar,
  Spinner,
  GAME_UI_ASSET_BASE,
  MemberAvatar,
};
export type { ChronicleSection, CouncilSection, DossierSection, IconName, PageAction, WorldSection };
